<template>
  <v-dialog
    v-model="newUserForm"
    max-width="1200px"
    persistent
    transition="dialog-bottom-transition"
  >
    <v-card>
      <v-toolbar dark color="secondary">
        <v-card-title>
          <i class="fal fa-users mr-2"></i>
          &nbsp;
          <span class="headline">Create New User</span>
        </v-card-title>
        <v-spacer></v-spacer>
        <button
          type="button"
          class="btn btn-light btn-elevate-hover btn-circle btn-icon btn-sm"
          @click="close"
        >
          <i class="fal fa-times"></i>
        </button>
      </v-toolbar>
      <v-card-text>
        <v-container class="kt-form">
          <!-- User's Info -->
          <h4 class="mt-5">
            <span>
              <i class="fal fa-info-circle"></i>
            </span>
            User's Info
          </h4>
          <v-row>
            <v-col cols="12" sm="6" md="6">
              <v-text-field
                v-model="editedItem.name"
                label="Name"
                v-validate="'required'"
                name="name"
              ></v-text-field>
              <span
                class="kt-font-danger validate-error"
                v-show="errors.has('name')"
                >{{ errors.first('name') }}</span
              >
            </v-col>
            <v-col cols="12" sm="6" md="6">
              <v-text-field
                v-model="editedItem.job_title"
                label="Job Title"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="6">
              <v-text-field
                v-model="editedItem.email"
                label="Email Address"
                v-validate="'required|email'"
                name="email"
              ></v-text-field>
              <span
                class="kt-font-danger validate-error"
                v-show="errors.has('email')"
                >{{ errors.first('email') }}</span
              >
            </v-col>
            <v-col cols="12" sm="6" md="6">
              <v-text-field
                v-model="editedItem.phone_number"
                label="Phone Number"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="6">
              <v-select
                v-model="editedItem.dealer_id"
                :items="dealerList"
                item-text="name"
                item-value="id"
                @change="removeSelectedSpecificDealer"
                label="Select a Company"
              />
            </v-col>
            <v-col cols="12" sm="6" md="6" v-show="isAutomotive || isAdmin">
              <multiselect
                v-model="editedItem.specific_dealer_id"
                tag-placeholder="Add this as the dealer you are part of."
                placeholder="Name of Your Dealership"
                label="name"
                :options="specificDealerList"
                :multiple="false"
                :taggable="true"
                :limit="2"
                @search-change="searchSpecificDealer"
                @tag="addNewSpecificDealer"
                track-by="id"
                id="specificDealerSelect"
              >
                <template slot="noOptions">Select Dealer</template>
              </multiselect>
            </v-col>
            <v-col cols="12" sm="6" md="6">
              <v-text-field
                v-model="editedItem.password"
                :append-icon="showPassword ? 'fal fa-eye' : 'fal fa-eye-slash'"
                :type="showPassword ? 'text' : 'password'"
                @click:append="showPassword = !showPassword"
                label="Password"
                v-validate="'required'"
                name="password"
                ref="password"
              ></v-text-field>
              <span
                class="kt-font-danger validate-error"
                v-show="errors.has('password')"
                >{{ errors.first('password') }}</span
              >
            </v-col>
            <v-col cols="12" sm="6" md="6">
              <v-text-field
                v-model="editedItem.password_confirmation"
                :append-icon="
                  showConfirmPassword ? 'fal fa-eye' : 'fal fa-eye-slash'
                "
                :type="showConfirmPassword ? 'text' : 'password'"
                @click:append="showConfirmPassword = !showConfirmPassword"
                label="Confirm Password"
                name="password_confirmation"
                v-validate="'required|confirmed:password'"
              ></v-text-field>
              <span
                class="kt-font-danger validate-error"
                v-show="errors.has('password_confirmation')"
                >{{ errors.first('password_confirmation') }}</span
              >
            </v-col>
            <v-col cols="12" sm="6" md="6">
              <v-file-input
                label="Upload a Profile Picture (optional)"
              ></v-file-input>
            </v-col>
            <v-col cols="12" sm="6" md="6">
              <v-select
                v-model="editedItem.roles"
                :items="roleList"
                item-text="friendly_name"
                item-value="id"
                label="Select a Role"
                multiple
                v-validate="'required'"
                name="role"
              ></v-select>
              <span
                class="kt-font-danger validate-error"
                v-show="errors.has('role')"
                >{{ errors.first('role') }}</span
              >
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue-grey darken-1" text @click="close">Cancel</v-btn>
        <v-btn color="blue darken-1" text @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import {
  each as lodashEach,
  head as lodashHead,
  filter as lodashFilter,
  map as lodashMap,
  pick as lodashPick,
  startCase as lodashStartCase,
  toLower as lodashToLower,
  some as _some,
  remove as _remove,
} from 'lodash-es';
import dayjs from 'dayjs';

export default {
  name: 'UserBase',
  props: {
    value: Boolean,
  },
  data: () => ({
    showPassword: false,
    showConfirmPassword: false,
    dealerList: [],
    roleList: [],
    specificDealerListSource: [],
    specificDealerList: [],
    allSpecificDealerList: [],
    editedIndex: -1,
    editedItem: {
      id: 0,
      name: '',
      job_title: '',
      email: '',
      phone_number: '',
      dealer_id: '',
      specific_dealer_id: '',
      password: '',
      password_confirmation: '',
      profilePic: '',
      roles: '',
      status: '',
      lmsAccess: false,
      secretShopAccess: false,
    },
  }),

  computed: {
    currentUser() {
      return this.$auth.user();
    },
    roles() {
      return this.currentUser.roles;
    },
    isSalesperson() {
      return _some(this.roles, { name: 'salesperson' });
    },
    isAutomotive() {
      return this.currentUser.dealer !== null
        ? this.currentUser.dealer.is_automotive
        : false;
    },
    isSpecificDealerManager() {
      return _some(this.roles, { name: 'specific-dealer-manager' });
    },
    isAdmin() {
      return _some(this.roles, { name: 'super-administrator' });
    },
    newUserForm: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },

  created() {
    this.initialize();
  },

  methods: {
    customSort(items, index, isDesc) {},

    searchSpecificDealer(query) {
      if (query == null || query.length <= 2) {
        this.specificDealerList = [];
        return;
      }

      const dealerId = this.editedItem.dealer_id;
      this.specificDealerList = lodashFilter(
        this.specificDealerListSource,
        (specificDealer) =>
          specificDealer.dealerId === dealerId &&
          specificDealer.name.indexOf(query) !== 1
      );
    },
    addNewSpecificDealer(newDealer) {
      const dealer = {
        id: newDealer,
        name: newDealer,
      };

      this.specificDealerListSource.push(dealer);
      this.specificDealerList.push(dealer);
      this.editedItem.specific_dealer_id = dealer;
    },
    initialize() {
      this.getSpecificDealers();
      this.getRoles();
      this.getDealers();
    },

    getSpecificDealers() {
      this.$http.get('/dealers/specific').then((response) => {
        lodashEach(response.data, (dealer) => {
          this.allSpecificDealerList.push({
            name: dealer.name,
            id: dealer.id,
          });
        });
      });
    },
    getDealers() {
      this.$http.get('/dealers').then((response) => {
        lodashEach(response.data, (dealer) => {
          this.dealerList.push({
            name: dealer.name,
            id: dealer.id,
          });
          lodashEach(dealer.specific_dealers, (specificDealer) => {
            this.specificDealerListSource.push({
              dealerId: dealer.id,
              name: specificDealer.name,
              id: specificDealer.id,
            });
          });
        });
      });
    },
    getRoles() {
      this.$http.get('/roles').then((response) => {
        lodashEach(response.data, (role) => {
          if (
            _some(this.roles, { name: 'account-manager' }) ||
            _some(this.roles, { name: 'specific-dealer-manager' })
          ) {
            if (this.isAutomotive && role.name === 'specific-dealer-manager') {
              this.roleList.push(lodashPick(role, ['id', 'friendly_name']));
            }

            if (
              role.name === 'account-manager' ||
              role.name === 'salesperson'
            ) {
              this.roleList.push(lodashPick(role, ['id', 'friendly_name']));
            }
          } else if (_some(this.roles, { name: 'super-administrator' })) {
            this.roleList.push(lodashPick(role, ['id', 'friendly_name']));
          }
        });
        if (this.isSpecificDealerManager) {
          this.roleList = _remove(this.roleList, (role) => {
            return role.friendly_name !== 'Account Manager';
          });
        }
      });
    },
    getRoleFriendlyName(roles) {
      const { roleList } = this;
      const friendlyNames = [];
      lodashEach(roles, (roleId) => {
        friendlyNames.push(lodashHead(lodashFilter(roleList, ['id', roleId])));
      });

      return lodashMap(friendlyNames, 'friendly_name').join(', ');
    },
    getDealerName(dealerId) {
      const { dealerList } = this;
      const dealer = lodashHead(lodashFilter(dealerList, ['id', dealerId]));
      return dealer === undefined ? '' : dealer.name;
    },
    getSpecificDealerName(specificDealerId) {
      const { allSpecificDealerList } = this;
      const dealer = lodashHead(
        lodashFilter(allSpecificDealerList, ['id', specificDealerId])
      );
      return dealer === undefined ? '' : dealer.name;
    },
    removeSelectedSpecificDealer() {
      this.editedItem.specific_dealer_id = null;
    },

    close() {
      this.newUserForm = false;
    },

    save() {
      const headers = {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      };
      const storeNewUserUrl = '/users/';
      this.$http
        .post(storeNewUserUrl, this.editedItem, {
          headers: headers,
        })
        .then((response) => {
          // eslint-disable-next-line camelcase
          this.$notify('success', 'New user successfully created.');
          this.close();
        })
        .catch((errors) => {
          const messages = errors.response.data.errors;
          lodashEach(messages, (value, key) => {
            this.errors.add({
              field: key,
              msg: lodashHead(value),
            });
          });
        });
    },
  },
};
</script>
<style lang="stylus" scoped>
.multiselect {
  margin-top: 9px;
}

.v-icon.v-icon {
  font-size: 16px;
}

.user-btn {
  margin: 0px 4px;
}

@media screen and (max-width: 1388px) {
  .user-btn {
    display: block !important;
    margin: 4px auto;
  }
}

@media screen and (max-width: 599px) {
  .user-btn {
    display: inline-block !important;
    margin: 0px 4px;
  }
}
</style>
